import { Component, inject, OnInit, effect } from '@angular/core';
import { RouterOutlet } from '@angular/router';
// import { NotificationToastComponent } from './features/notifications/components/notification-toast/notification-toast.component';
import { NotificationStoreService } from './domains/notification/services/notification-store.service';
import { AuthService } from './core/auth/services/auth.service';
import { ViewportParityService } from './core/platform/viewport-parity.service';
import { PlatformPerformanceService } from './core/platform/platform-performance.service';
import { VideoPlaybackRumService } from './shared/services/video-playback-rum.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  private readonly notificationStore = inject(NotificationStoreService);
  private readonly authService = inject(AuthService);
  private readonly viewportParity = inject(ViewportParityService);
  private readonly platformPerformance = inject(PlatformPerformanceService);
  private readonly videoPlaybackRum = inject(VideoPlaybackRumService);

  constructor() {
    // Initialize notifications when user becomes authenticated
    effect(() => {
      const isAuthenticated = this.authService.isAuthenticated();
      if (isAuthenticated) {
        this.notificationStore.initializeNotifications();
      }
    });
  }

  ngOnInit(): void {
    // Publishes --kb-inset so iOS matches Android when the keyboard opens.
    this.viewportParity.init();

    // Publishes .gpu-ok / .is-scrolling so the native scroll performance layer
    // in styles.scss can drop expensive paint work per device and per gesture.
    this.platformPerformance.init();

    // Attaches a passive, batched listener for quality-first modal-video telemetry.
    this.videoPlaybackRum.init();

    // Unlock browser audio context on first user interaction so hover video playback with audio works seamlessly across all devices
    this.setupGlobalAudioUnlocker();

    // Only load notifications if user is already authenticated on init
    if (this.authService.isAuthenticated()) {
      this.notificationStore.initializeNotifications();
    }
  }

  private setupGlobalAudioUnlocker(): void {
    if (typeof window === 'undefined') return;

    const unlockAudio = () => {
      try {
        const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        if (AudioContextClass) {
          const ctx = new AudioContextClass();
          if (ctx.state === 'suspended') {
            void ctx.resume();
          }
        }
      } catch {
        // Ignore unsupported environments
      }
    };

    const opts = { once: true, passive: true, capture: true };
    window.addEventListener('pointerdown', unlockAudio, opts);
    window.addEventListener('keydown', unlockAudio, opts);
    window.addEventListener('touchstart', unlockAudio, opts);
  }
}
